<script>
import Product from '../common/Product';
import MixedGridFilters from './MixedGridFilters';
import MixedGrid from '@phoenix/mixins/components/mixedGrid/MixedGrid';
import { map } from 'lodash-es';
import MixedGridImageItem from './MixedGridImageItem';
import MixedGridVideoItem from './MixedGridVideoItem';

export default {
  components: {
    MixedGridImageItem,
    MixedGridVideoItem,
    MixedGridFilters,
    Product,
  },

  mixins: [MixedGrid],

  props: {
    alignedCenter: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      pageInnerWidth: window.innerWidth,
    };
  },

  computed: {
    loadMoreClasses() {
      return [
        'text-center mt-24',
        map(this.maxPage, (page, size) => {
          const prefix = size !== 'xs' ? `${size}:` : '';
          return this.page >= page ? `${prefix}hidden` : `${prefix}block`;
        }),
      ];
    },

    gridClass() {
      return [
        'menu--md flex-grid flex-grid--x--sm md:flex-grid--x--md flex-grid--y--sm md:flex-grid--y--md flex-grid--even',
        { 'flex-grid--center': this.alignedCenter },
      ];
    },

    /**
     * Available widths for each breakpoints
     */
    widths() {
      return {
        lg: {
          1: '1/4',
          2: '1/2',
          3: '3/4',
          4: 'full',
        },
        md: {
          1: '1/3',
          2: '2/3',
          3: 'full',
        },
        sm: {
          1: '1/2',
          2: 'full',
        },
        xs: {
          1: '1/2',
          2: 'full',
        },
      };
    },
  },

  created() {
    window.addEventListener('resize', () => (this.pageInnerWidth = window.innerWidth));
  },

  methods: {
    /**
     * Construct the classes used to define the sizes of columns for each breakpoints.
     */
    getSizes(column) {
      if (this.areSortOrFiltersTouched) {
        return `xs:w-${this.widths.xs[1]} sm:w-${this.widths.sm[1]} md:w-${this.widths.md[1]} lg:w-${this.widths.lg[1]}`;
      }

      const sm = column.sizes.sm ? column.sizes.sm : 1;
      const md = column.sizes.md ? column.sizes.md : 1;
      const lg = column.sizes.lg ? column.sizes.lg : 1;
      const xs = column.sizes.xs ? column.sizes.xs : 1;

      return `xs:w-${this.widths.xs[xs]} sm:w-${this.widths.sm[sm]} md:w-${this.widths.md[md]} lg:w-${this.widths.lg[lg]}`;
    },

    getColumnClasses(column) {
      return ['w-full', this.getSizes(column), this.getDisplayedColumnClasses(column)];
    },

    getDisplayedColumnClasses(column) {
      if (!this.shouldDisplayLoadMore) {
        return;
      }

      return map(column.page, (page, size) => {
        const prefix = size !== 'xs' ? `${size}:` : '';
        return this.page < page ? `${prefix}hidden` : `${prefix}flex`;
      });
    },

    getComponent(template) {
      switch (template) {
        case 'image-fit':
          return MixedGridImageItem;
        case 'video-item':
          return MixedGridVideoItem;
      }
    },
  },
};
</script>

<template>
  <mixed-grid-filters
    v-if="displayFilter"
    :filters="filtersWithLocalizedPrices"
    :products-count="filteredColumnsCount"
    :unique-id="uniqueId"
    :default-sort="defaultSort"
    :default-filters="activeFilters"
    :should-display-sellable="shouldDisplaySellable"
    :has-filters-in-url="hasFiltersInUrl"
    @on-filters-changed="onFiltersChanged"
  />
  <section class="section bg-black text-white">
    <div class="container container--tight px-4 md:px-10" data-cy="mixed-grid">
      <div v-if="title || subtitle" class="simple-section simple-section--tight">
        <div class="container">
          <div class="simple-section__body">
            <h3 v-if="title" class="simple-section__title" v-html="title" />

            <div v-if="subtitle" class="simple-section__text">
              <div class="mt-4">
                <p class="mt-12" v-html="subtitle" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <ul :class="gridClass">
        <li
          v-for="column in filteredColumns"
          :key="column.data.itemId"
          :class="getColumnClasses(column)"
          data-cy="mixed-grid-item"
          :data-cy-item="column.template"
        >
          <mixed-grid-image-item
            v-if="column.template === 'image-fit'"
            v-bind="{ ...column.data, image: getImage(column) }"
          />

          <mixed-grid-video-item v-if="column.template === 'video-item'" v-bind="column.data" />

          <product
            v-if="column.template === 'product'"
            v-bind="column.data"
            :data-tracking="getDataTracking(column.data)"
            :is-dark="true"
          />
        </li>
      </ul>
      <div v-if="shouldDisplayLoadMore" :class="loadMoreClasses">
        <div class="sgphx">
          <div class="sgphx-actions sgphx-actions--row sgphx-actions--center">
            <button type="button" class="sgphx-btn sgphx-btn--primary" data-cy="mixed-grid-load-more" @click="nextPage">
              <span class="sgphx-btn__text">
                <span>{{ $t('Button.loadMore') }}</span>
              </span>
            </button>

            <button
              class="sgphx-btn sgphx-btn--primary sgphx-btn--positive"
              data-cy="mixed-grid-see-all"
              @click="seeAll"
            >
              <span class="sgphx-btn__text">
                <span>{{ $t('Button.seeAll') }}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
